import type { CdekTrackingData } from './api/schemas/cdek.schema'
import {
  CDEK_STATUS_VOCABULARY,
  cdekStatusLabel,
  classifyCdekStatus,
  UNKNOWN_CDEK_STATUS_LABEL_KEY,
  type CdekHandoffFact,
} from './cdek-status-vocabulary.generated'

export type CdekProviderStatus = NonNullable<CdekTrackingData['statuses']>[number]

/**
 * The one place the browser may read a provider status list.
 *
 * `GET /api/cdek/track` returns the provider document verbatim — the shape
 * `docs/client-call-site-inventory.md` records — and the order of its
 * `statuses` array is not a contract. These helpers answer "latest" and "first
 * accepted" from the provider's own `date_time`, so no renderer depends on the
 * array position that thirty observed payloads happened to agree on.
 */
function providerTime(status: CdekProviderStatus): number {
  const parsed = Date.parse(status.date_time || '')
  return Number.isFinite(parsed) ? parsed : Number.NaN
}

export function latestCdekStatus(
  statuses?: readonly CdekProviderStatus[] | null,
): CdekProviderStatus | undefined {
  let latest: CdekProviderStatus | undefined
  let latestAt = Number.NEGATIVE_INFINITY
  for (const status of statuses || []) {
    const at = providerTime(status)
    if (Number.isNaN(at)) {
      // An undated entry never outranks a dated one, and only stands in when
      // nothing dated exists at all.
      if (latest === undefined) latest = status
      continue
    }
    if (at > latestAt) {
      latestAt = at
      latest = status
    }
  }
  return latest
}

function handoffOf(status: CdekProviderStatus): CdekHandoffFact {
  return classifyCdekStatus(status.code)?.handoff ?? 'not_yet'
}

export function firstAcceptedCdekStatus(
  statuses?: readonly CdekProviderStatus[] | null,
): CdekProviderStatus | undefined {
  let earliest: CdekProviderStatus | undefined
  let earliestAt = Number.POSITIVE_INFINITY
  for (const status of statuses || []) {
    if (handoffOf(status) === 'not_yet') continue
    const at = providerTime(status)
    if (Number.isNaN(at) || at >= earliestAt) continue
    earliestAt = at
    earliest = status
  }
  return earliest
}

/** The generated map is the only client-side source of Russian status copy. */
export function cdekStatusLabelForCode(code?: string | null, city?: string | null): string {
  const facts = classifyCdekStatus(code)
  return cdekStatusLabel(facts?.labelKey ?? UNKNOWN_CDEK_STATUS_LABEL_KEY, city)
}

export function isKnownCdekStatusCode(code?: string | null): boolean {
  return (code || '').trim().toUpperCase() in CDEK_STATUS_VOCABULARY
}
